import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../services/admin.service';
import { Global } from '../../../app.global';
import { HelperService } from '../../../shared/common/services/helper.service';
import { flashAnimation } from '../../../app.animations';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SettingsService } from '../../../authentication/services/settings.service';

@Component({
  selector: 'app-nexus-component',
  templateUrl: './nexus.component.html',
  styleUrls: ['./nexus.component.scss'],
  animations: [flashAnimation],
})
export class NexusComponent implements OnInit {
  questionAgentEnabled = false;
  showDocumentConfidenceEnabled = false;

  nexusUploadDefault: 'dragdrop' | 'freetext' | 'link' = 'dragdrop';
  maxOutputTokens = 200;
  selectedModel = 'gpt4omini';
  temperature = 0.1;
  selectedTab = 'general';
  isUploading = false;
  dragOverTag: string | null = null;
  aiTags: string[] = [
    'Hire Agreement',
    'Disabled Badge',
    'Pay by phone receipt',
    'Delivery Note',
    'Invoice',
  ];
  otherTags: string[] = [
    'About Condition',
    'Apple Permit2',
    'Badge',
    'Bank Statement',
    'Breakdown Co Report',
    'Business',
    'Cash PCN Payment',
    'Challenge',
    'Cheque - Copy Of Both Sides',
    'Contract',
    'Counterfoil',
    'Custom Virtual Permit',
    'Delivery Note',
    'Disabled Badge',
    'Dispensation',
    'Display Item',
    'Doctor',
    'Driving License',
    'DVLA Letter',
    'Form 4737',
    'Goods',
    "Health Emerg'y Badge",
    'Hire Agreement',
    'Hospital Appointment',
    'Insurance Doc',
    'Job Sheet',
    'Job Sheet - Cash Carrying',
    'Job Sheet - Stat Undertaking',
    'Job Sheet - Utility',
    'Job Sheet/Letter - Medical',
    'Letter From Medic',
    'Letter From Senior',
    'Letter From Superior',
    'Low Carbon Neighbourhood Permit',
    'Market Trader',
    'Medical',
    'NTO',
    'Other',
    'Parts',
    'Passport',
    'Pay & Display Ticket',
    'Payment Related',
    'Permit',
    'Police Document',
    'Postal Order',
    'Post Office Report',
    'Private-Hire Licence',
    'Proof Of',
    'Proof Of Charging Session',
    'Proof Of Funeral Service',
    'Proof Of Road User Charge Payment',
    'Receipt/Invoice For',
    'Removal',
    'Repair',
    'Representations Form',
    'Resident',
    'Sale/Purchase Of Car',
    'Season Ticket',
    "Smart Card Visitor's Ticket",
    "Special Visitor's Permit",
    'Staff Permit',
    'Tax Disc',
    'Test Permit',
    'Theft - Proof Of',
    'Vehicle Registration Doc',
    'V5C Document',
    'Visitors Custom Permit',
    'Visitor',
    'Voucher',
    'Whitelist Exemption Notification',
    'Zoo Permit',
    'Other',
  ];

  selectedGenericTag = '';

  constructor(
    public global: Global,
    private settingsService: SettingsService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.isUploading = false;
    this.loadInitialSettings();
  }

  loadInitialSettings() {
    this.settingsService.getApplicationSetting('Nexus.Model').subscribe((value) => {
      if (typeof value === 'string') {
        this.selectedModel = value;
      }
    });

    this.settingsService.getApplicationSetting('Nexus.MaxOutputTokens').subscribe((value) => {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        this.maxOutputTokens = parsed;
      }
    });

    this.settingsService.getApplicationSetting('Nexus.Temperature').subscribe((value) => {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        this.temperature = parsed;
      }
    });

    this.settingsService.getApplicationSetting('NexusAnswerQuestions').subscribe((value) => {
      this.questionAgentEnabled = value === true || value === 'true' || value === '1';
    });

    this.settingsService
      .getApplicationSetting('Nexus.ShowDocumentConfidence')
      .subscribe((value) => {
        this.showDocumentConfidenceEnabled = value === true || value === 'true' || value === '1';
      });

    this.settingsService.getApplicationSetting('NexusDragDropDefault').subscribe((value) => {
      const stringValue = String(value).toLowerCase();

      if (stringValue === 'dragdrop' || stringValue === 'freetext' || stringValue === 'link') {
        this.nexusUploadDefault = stringValue as 'dragdrop' | 'freetext' | 'link';
      } else {
        this.nexusUploadDefault = 'dragdrop';
      }
    });
  }

  switchTab(tab: string) {
    this.selectedTab = tab;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFileDrop(event: DragEvent, tag: string) {
    event.preventDefault();
    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      const file = files[0];
      this.uploadTrainingFile(file, tag);
    }
  }

  onDragEnter(tag: string) {
    this.dragOverTag = tag;
  }

  onDragLeave(tag: string) {
    if (this.dragOverTag === tag) {
      this.dragOverTag = null;
    }
  }

  uploadTrainingFile(file: File, tag: string) {
    this.isUploading = true;
    const uploadUrl = `${environment.apiPath}api/nexus/uploadTrainingFile?tag=${encodeURIComponent(tag)}`;

    const token = localStorage.getItem('rm_access_token');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token || ''}`,
      Accept: 'application/json',
    });

    const formData = new FormData();
    formData.append('file', file);

    this.http.post(uploadUrl, formData, { headers }).subscribe({
      next: () => alert(`Uploaded ${file.name} to tag "${tag}"`),
      error: (err) => {
        console.error(err);
        alert('Upload failed');
      },
      complete: () => {
        this.isUploading = false;
      },
    });
  }

  toggleSetting(identifier: string, value: string): void {
    this.settingsService.updateAppSetting(identifier, value).subscribe({
      next: (success) => {
        if (!success) {
          alert('Failed to update setting');
          return;
        }

        if (identifier === 'NexusDragDropDefault') {
          const validValues = ['dragdrop', 'freetext', 'link'] as const;
          if (validValues.includes(value as any)) {
            this.nexusUploadDefault = value as (typeof validValues)[number];
          }
        } else if (identifier === 'NexusAnswerQuestions') {
          this.questionAgentEnabled =
            (typeof value === 'boolean' && value === true) ||
            (typeof value === 'string' && (value === 'true' || value === '1'));
        } else if (identifier === 'Nexus.ShowDocumentConfidence') {
          this.showDocumentConfidenceEnabled =
            (typeof value === 'boolean' && value === true) ||
            (typeof value === 'string' && (value === 'true' || value === '1'));
        }
      },
      error: (err) => {
        console.error(err);
        alert('Error updating setting');
      },
    });
  }

  onMaxOutputTokensChange() {
    this.settingsService
      .updateAppSetting('Nexus.MaxOutputTokens', String(this.maxOutputTokens))
      .subscribe({
        next: (success) => {
          if (!success) {
            alert('Failed to update Max Output Tokens');
          }
        },
        error: (err) => {
          console.error(err);
          alert('Error updating Max Output Tokens');
        },
      });
  }

  onModelChange() {
    this.settingsService.updateAppSetting('Nexus.Model', this.selectedModel).subscribe({
      next: (success) => {
        if (!success) {
          alert('Failed to update Model');
        }
      },
      error: (err) => {
        console.error(err);
        alert('Error updating Model');
      },
    });
  }

  onTemperatureChange() {
    this.settingsService.updateAppSetting('Nexus.Temperature', String(this.temperature)).subscribe({
      next: (success) => {
        if (!success) {
          alert('Failed to update temperature');
        }
      },
      error: (err) => {
        console.error(err);
        alert('Error updating temperature');
      },
    });
  }
}
